import { useCallback, useEffect, useState } from 'react'
import { ItemOption } from 'components/FormInputSelect'
import generalDataService from 'services/general-data.service'
import { hearingGeneralDataInit, HearingGeneralDataType } from 'types/hearing-general-data.type'
import genericService from 'services/generic.service'
import districtAttorneyService from 'services/district-attorney.service'
import { useBackdrop } from 'contexts/backdrop.context'
import { useSnackBar } from 'contexts/snackbar.context'
import { errorColor, errorMessage } from 'utils/error.util'
import { HttpErrorType } from 'types/http.type'
import { SystemMessages } from 'utils/enums/system-message.enum'

export function useHearingGeneralData(hearingId?: string) {
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()
  const { showSnackBar } = useSnackBar()

  const [hearingGeneralData, setHearingGeneralData] = useState<HearingGeneralDataType | null>(null)
  const [craaiOptions, setCraaiOptions] = useState<ItemOption[]>([])
  const [districtAttorneyOptions, setDistrictAttorneyOptions] = useState<ItemOption[]>([])

  const getGeneralData = useCallback(async () => {
    try {
      await generalDataService.getById(hearingId).then((response) => {
        const initialValues = response.data ? response.data : hearingGeneralDataInit
        setHearingGeneralData(initialValues)
      })
    } catch (err) {
      console.log('General Data Request: ', err)
    }
  }, [hearingId])

  const fillCraaiOptions = async (promotoria?: string) => {
    try {
      if (promotoria !== undefined) {
        return await genericService.get(`/craai/${promotoria}`).then((res) => res.data)
      } else {
        return await genericService.get('/craai').then((res) => res.data)
      }
    } catch (error) {
      console.error('Craai Request: ', error)
    }
  }

  const fillDistrictAttorneysOptions = async (craai?: string | null) => {
    try {
      if (!craai || craai === '0') return
      return await districtAttorneyService.getAll(craai).then((res) => res.data)
    } catch (error) {
      console.error('District Attorney Request: ', error)
    }
  }

  const handleDistrictAttorneyOptions = (data: ItemOption[]) => {
    setDistrictAttorneyOptions(data)
  }

  const saveGeneralData = async (data: HearingGeneralDataType): Promise<void> => {
    if (hearingId) {
      handleBackdropOpen()
      await generalDataService
        .save(hearingId, data)
        .then(async () => {
          showSnackBar(SystemMessages.MESSAGE_001, 'success')
          await getGeneralData()
        })
        .catch((error: HttpErrorType) => {
          showSnackBar(errorMessage(error), errorColor(error))
        })
        .finally(() => handleBackdropClose())
    }
  }

  useEffect(() => {
    ;(async () => {
      await getGeneralData()
      await fillCraaiOptions().then((response) => response && setCraaiOptions(response))
    })()
  }, [getGeneralData])

  return {
    hearingGeneralData,
    craaiOptions,
    districtAttorneyOptions,

    fillCraaiOptions,
    fillDistrictAttorneysOptions,

    handleDistrictAttorneyOptions,

    saveGeneralData,
  }
}
